/*
 * <pdp-buy-bar> — mobile sticky buy bar for the PDP.
 *
 * Mirrors, never duplicates: the bar reflects the main Dawn ATC button and
 * the Northbound product price surface, and forwards clicks to the real
 * submit button. Nothing here computes prices or talks to the cart.
 * See snippets/pdp-buy-bar.liquid and docs/pdp-content.md.
 */
if (!customElements.get('pdp-buy-bar')) {
  customElements.define(
    'pdp-buy-bar',
    class PdpBuyBar extends HTMLElement {
      static MOBILE_QUERY = '(max-width: 749px)';

      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onClick = this.onClick.bind(this);
        this.sync = this.sync.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
      }

      connectedCallback() {
        this.source = document.getElementById(this.dataset.sourceButton);
        this.info = document.getElementById(this.dataset.sourceInfo);
        this.button = this.querySelector('[data-pdp-buy-bar-button]');
        this.label = this.querySelector('[data-pdp-buy-bar-label]');
        this.price = this.querySelector('[data-pdp-buy-bar-price]');
        this.media = window.matchMedia(PdpBuyBar.MOBILE_QUERY);

        if (!this.source || !this.button) return;

        this.button.addEventListener('click', this.onClick);

        // Dawn owns the error message. Bring a newly displayed error into
        // view when shoppers are using the bar below the main form.
        this.errorWrapper = this.source.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        this.lastError = this.errorText;
        if (this.errorWrapper) {
          this.errorObserver = new MutationObserver(() => this.revealError());
          this.errorObserver.observe(this.errorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }

        // Reflect ATC state (label, disabled, loading) whenever Dawn or
        // Northbound mutate the real button.
        this.sourceObserver = new MutationObserver(this.scheduleSync);
        this.sourceObserver.observe(this.source, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        });

        // Reflect the price text. product-info.js swaps the price block on
        // variant change and Northbound rewrites the display slot on
        // community change, so watch the info column rather than one node.
        if (this.info) {
          this.infoObserver = new MutationObserver(this.scheduleSync);
          this.infoObserver.observe(this.info, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        }

        this.media.addEventListener('change', this.onMediaChange);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        this.sync();
        this.onScroll();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.sourceObserver?.disconnect();
        this.infoObserver?.disconnect();
        this.errorObserver?.disconnect();
        this.media?.removeEventListener('change', this.onMediaChange);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.setVisible(false);
      }

      get priceSource() {
        return (
          this.info?.querySelector(
            '[data-northbound-price-surface="product"] [data-northbound-price-display]'
          ) || null
        );
      }

      get errorText() {
        if (!this.errorWrapper || this.errorWrapper.hidden) return '';
        return this.errorWrapper.querySelector('.product-form__error-message')?.textContent.trim() || '';
      }

      revealError() {
        const message = this.errorText;
        const changed = message !== this.lastError;
        this.lastError = message;
        if (!changed || !message || !this.isConnected || !this.visible || !this.media.matches) return;

        // Use the existing alert instead of duplicating it in a second live
        // region. Clearing/retrying remains entirely owned by product-form.
        this.errorWrapper.setAttribute('tabindex', '-1');
        this.errorWrapper.scrollIntoView({ block: 'center', behavior: 'instant' });
        this.errorWrapper.focus({ preventScroll: true });
        this.onScroll();
      }

      scheduleSync() {
        if (this.syncScheduled) return;
        this.syncScheduled = true;
        requestAnimationFrame(() => {
          this.syncScheduled = false;
          this.sync();
        });
      }

      sync() {
        if (!this.source) return;

        const sourceLabel = this.source.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
        if (sourceLabel && this.label) {
          const text = sourceLabel.textContent.trim();
          if (text && this.label.textContent !== text) this.label.textContent = text;
        }

        const unavailable =
          this.source.hasAttribute('disabled') || this.source.getAttribute('aria-disabled') === 'true';
        this.button.disabled = unavailable;
        this.button.classList.toggle('loading', this.source.classList.contains('loading'));

        const priceSource = this.priceSource;
        if (priceSource && this.price) {
          const text = priceSource.textContent.trim();
          if (text && this.price.textContent !== text) this.price.textContent = text;
          const placeholder = priceSource.hasAttribute('data-northbound-price-placeholder');
          this.price.toggleAttribute('data-pdp-buy-bar-placeholder', placeholder);
        }
      }

      onMediaChange() {
        this.onScroll();
      }

      onScroll() {
        if (!this.source || !this.media.matches) {
          this.setVisible(false);
          return;
        }
        const rect = this.source.getBoundingClientRect();
        // Show once the real button has scrolled above the viewport; keep
        // hidden while it is still below (top of page) or on screen.
        this.setVisible(rect.bottom < 0);
      }

      setVisible(visible) {
        if (this.visible === visible) return;
        this.visible = visible;
        if (visible) {
          this.hidden = false;
          // Two frames so the transform transition runs from the hidden state.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.classList.add('is-visible'));
          });
          document.documentElement.classList.add('pdp-buy-bar-visible');
        } else {
          this.classList.remove('is-visible');
          document.documentElement.classList.remove('pdp-buy-bar-visible');
          const hide = () => {
            if (!this.visible) this.hidden = true;
          };
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (reduced) hide();
          else setTimeout(hide, 220);
        }
      }

      onClick(event) {
        event.preventDefault();
        if (!this.source || this.button.disabled) return;
        if (this.source.getAttribute('aria-disabled') === 'true') return;
        // Forward to the real Dawn submit button so product-form.js owns
        // the request, the loading state and the cart notification.
        this.source.click();
      }
    }
  );
}
